import React, { useEffect, useMemo, useRef } from 'react';
import { getInstanceByDom } from 'echarts/core';
import classnames from 'classnames';
import moment from 'moment';
import { useIntl } from 'umi';
import { Bubble } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import Loading from '@intelligence/components/Loading';
import NoData from '@intelligence/components/NoData';
import DataCompareBubble from '@intelligence/v3/components/DataCompareBubble';
import MetricsCardTrendChart from '@/pages/dashboard/components/MetricsCardTrendChart';
import { scaleToPercent, formatLimitedRatioDisplay } from '../../utils';

import './index.scss';

interface IChartDataItem {
  date: string;
  value: number;
  name: string;
}

interface IKpiCardProps extends IBaseProps {
  loading?: boolean;
  title: string;
  isFirstCard?: boolean;
  tipsI18nKey?: string;
  metricKey: string;
  granularity?: 'monthly' | 'daily' | (string & {});
  // 当前周期开始日期（用于环比气泡）
  startDate?: string;
  // 当前周期结束日期（用于环比气泡）
  endDate?: string;
  // 前一周期指标值（用于环比气泡）
  prevValue?: number;
  value?: number;
  ratio?: number;
  chartData?: IChartDataItem[];
  chartLoading?: boolean;
  formatValue?: (value: number | undefined, metricKey: string) => string;
  formatRule?: string;
}

const KpiCard: React.FC<IKpiCardProps> = (props) => {
  const {
    style,
    className,
    loading = true,
    title,
    isFirstCard = false,
    tipsI18nKey,
    metricKey,
    granularity = 'monthly',
    startDate,
    endDate,
    prevValue,
    value,
    ratio,
    chartData,
    chartLoading,
    formatValue,
    formatRule = 'thousands2Decimal',
  } = props;

  const { formatMessage } = useIntl();

  // 格式化显示值
  const displayValue = useMemo(() => {
    if (formatValue) {
      return formatValue(value, metricKey);
    }
    if (value === undefined || value === null) return '--';
    return value.toLocaleString();
  }, [value, metricKey, formatValue]);

  // 转换折线图数据格式为 MetricsCardTrendChart 期望的格式
  // MetricsTrendChart 期望格式: { date, metrics: { [metricKey]: { value } } }
  // 修复：MetricsCardTrendChart 内部使用 slice 截取日期，期望格式为 YYYYMM 或 YYYYMMDD
  // 月粒度：slice(4, 6) 取月份 → 需要 YYYYMM 格式
  // 日粒度：slice(-2) 取日期 → 需要 YYYYMMDD 格式
  const lineChartData = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    const formattedData = chartData.map((item) => {
      // 根据粒度转换日期格式
      let formattedDate = item?.date || '';
      if (granularity === 'monthly') {
        // "2024-01" 或 "2024-01-01" → "202401"
        formattedDate = moment(item?.date).format('YYYYMM');
      } else {
        // "2024-01-15" → "20240115"
        formattedDate = moment(item?.date).format('YYYYMMDD');
      }

      return {
        date: formattedDate,
        metrics: {
          [metricKey]: {
            value: item?.value,
          },
        },
      };
    });

    // 按日期升序排序（YYYYMM 或 YYYYMMDD 格式可直接字符串比较）
    formattedData.sort((a, b) => (a?.date || '').localeCompare(b?.date || ''));

    return formattedData;
  }, [chartData, metricKey, granularity]);

  // 构建 componentMetrics
  const componentMetrics = useMemo(() => {
    return [
      {
        metricKey,
        nameEn: title,
        nameCn: title,
        formatRule,
      },
    ];
  }, [metricKey, title, formatRule]);

  // 构建 filterVal
  const filterVal = useMemo(() => {
    return {
      timeType: granularity === 'daily' ? 'Day' : 'Month',
    };
  }, [granularity]);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  /** 仅在趋势图已加载时监听尺寸，避免 loading/空数据状态注册无效观察器。 */
  const showChart = !chartLoading && !!lineChartData?.length;

  // 公共 Chart 组件的 resize 依赖 ref.current.clientWidth（非响应式），flex 布局变化时不会自动触发 resize，
  // 导致 canvas 保持 init 时的宽度可能溢出。用 ResizeObserver 监听容器尺寸变化补偿。
  useEffect(() => {
    if (!showChart) return;
    const chartDom = chartContainerRef.current?.querySelector<HTMLElement>('.chart-box div[_echarts_instance_]');
    if (!chartDom) return;

    let rafId: number | null = null;

    const ro = new ResizeObserver(() => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        const chartInstance = getInstanceByDom(chartDom);
        const width = chartDom.clientWidth;
        const height = chartDom.clientHeight;
        if (!chartInstance || !width || !height) return;

        // 尺寸未变化时跳过 resize，避免打断 ECharts 首次 setOption 的渲染动画。
        if (chartInstance.getWidth() === width && chartInstance.getHeight() === height) return;

        chartInstance.resize({ animation: { duration: 300 } });
      });
    });
    ro.observe(chartDom);

    return () => {
      ro.disconnect();
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [showChart]);

  return (
    <CardCommon style={style} className={classnames('dbh-card-kpi', className)}>
      {tipsI18nKey ? (
        <Bubble
          style={{ maxWidth: 500 }}
          dark
          placement={isFirstCard ? 'top-start' : 'top'}
          arrowPointAtCenter={!isFirstCard}
          content={<span style={{ whiteSpace: 'pre-line' }}>{formatMessage({ id: tipsI18nKey })}</span>}
        >
          <span className="dbh-kpi-title" style={{ cursor: 'pointer' }}>
            {title}
          </span>
        </Bubble>
      ) : (
        <div className="dbh-kpi-title">{title}</div>
      )}
      <div className="dbh-kpi-content">
        <div className="dbh-kpi-info">
          <div className="dbh-kpi-value">{loading ? '-' : displayValue}</div>
          <div className="dbh-kpi-trend">
            <span className="dbh-kpi-trend-label">{formatMessage({ id: 'dbh_studio_casual_ratio' })}</span>
            {loading ? (
              <span>-</span>
            ) : (
              <DataCompareBubble
                granularity={granularity as IGranularity}
                startDate={startDate}
                endDate={endDate}
                title={title}
                value={value}
                prevValue={prevValue}
                valueFormatter={(val) => (formatValue ? formatValue(val as number, metricKey) : String(val ?? '--'))}
              >
                <span>
                  <PercentageItem
                    percentage={scaleToPercent(ratio)}
                    formateFunc={(currentValue) => formatLimitedRatioDisplay(currentValue)}
                  />
                </span>
              </DataCompareBubble>
            )}
          </div>
        </div>
        <div className="dbh-kpi-chart" ref={chartContainerRef}>
          {chartLoading ? (
            <Loading />
          ) : lineChartData && lineChartData.length > 0 ? (
            <MetricsCardTrendChart
              height={90}
              loading={false}
              error={false}
              isLeftCard={true}
              showConfig={{ showLineChart: true, showBubbleTitle: true }}
              filterVal={filterVal}
              metricData={lineChartData}
              componentMetrics={componentMetrics}
            />
          ) : (
            <NoData />
          )}
        </div>
      </div>
    </CardCommon>
  );
};

export default KpiCard;
